<script>
// Type definitions for the claim form and its entries are included at the bottom of this file for reference.
import Turbolinks from "turbolinks";
import { userProjectMixin } from "../mixins/userProject";
import { competitionStreamsMixin } from "../mixins/competitionStreams";

export default {
  name: "FundingProjectsFinanceShow",
  mixins: [userProjectMixin, competitionStreamsMixin],
  data() {
    return {
      /** @type {ClaimForm} */
      form: {
        labour: {},
        sub_contractors: [],
        travel: [],
        mileage: [],
        materials: [],
        other_costs: [],
        delivery_partners: {},
        forecast: {},
        soe_upload: null,
      },
      feedback: {
        labour: "",
        sub_contractors: "",
        travel: "",
        mileage: "",
        materials: "",
        other_costs: "",
        delivery_partners: "",
        forecast: "",
        general: "",
      },
      errors: {},
      processing: false,
      dirty: false,
      showReviewModal: false,
    };
  },
  computed: {
    /**
     * @description Check if the current claim is editable. This is used to disable form inputs and prevent submission if the claim is not in draft.
     * @returns {boolean}
     */
    canEdit() {
      return false;
    },

    /**
     * @description Check if the current claim can be approved. This is used to enable the approve and reject buttons if the claim is in progress and has a status of 2 (pending) or 3 (awaiting approval).
     * @returns {boolean}
     */
    canApprove() {
      return this.claim.is_in_progress && [1,2].includes(this.claim.status);
    },

    /**
     * @description Check if the current claim has any staff entries. This is used to determine if the staff section should be displayed.
     * @returns {boolean}
     */
    hasStaff() {
      return Object.keys(this.form.staff).length > 0;
    },

    /**
     * @description Check if the current claim has any delivery partner entries. This is used to determine if the delivery partners section should be displayed.
     * @returns {boolean}
     */
    hasInitialForecast() {
      return this.state.forecasts?.initials_completed ?? false;
    },

    /**
     * @description Check whether a SoE is required for this claim
     * @return {boolean}
     */
    requiresSoe() {
      return this.state.budgets?.some(budget => budget.requires_soe) ?? false;
    },

    /**
     * @description Get the current period for the claim. This is used to display the period in the UI.
     * @returns {string}
     */
    period() {
      return this.state.period ?? "";
    },

    /**
     * @description Get the project for the current claim. This is used to display the project details in the UI.
     * @returns {object}
     */
    project() {
      return this.state.project ?? {};
    },

    /**
     * @description Get the claim for the current period. This is used to display the claim details in the UI.
     * @returns {object}
     */
    claim() {
      return this.state.claim ?? {};
    },

    /**
     * @description Get the staff for the current claim. This is used to generate the staff form.
     * @returns {Array<StaffEntry>}
     */
    staff() {
      return this.state.staff ?? [];
    },

    /**
     * @description Get the delivery partners for the current claim. This is used to generate the delivery partners form.
     * @returns {Array<DeliveryPartnerEntry>}
     */
    deliveryPartners() {
      return this.state.delivery_partners ?? [];
    },

    /**
     * @description Get the forecasts for the current claim. This is used to generate the forecast form.
     * @returns {Forecasts}
     */
    forecasts() {
      return this.state.forecasts ?? {};
    },

    /**
     * @description Get the forecast options for the current claim. This is used to generate the forecast form.
     * @returns {Array<{key: string, name: string}>}
     */
    options() {
      return this.state.forecast_options ?? []
    },

    /**
     * @description Get the sections of the claim form. This is used to generate the navigation links for the claim form.
     * @returns {Array<{key: string, name: string, errors: number}>}
     */
    sections() {
      const sections = this.options.map((option) => {
        return {
          key: option.key,
          name: option.name,
          errors: this.errorsForKey(option.key),
        };
      })
      .filter((option) => option.key !== 'overheads')
      sections.push({
        key: 'forecast',
        name: 'Expenditure Forecast',
        errors: this.errorsForKey('forecast'),
      });
      return sections;
    },

    /**
     * @description Get the total cost for each section in the claim form. This is used to validate that the forecast does not exceed the claim total.
     * @returns {Object.<string, number>}
     */
    sectionCosts() {
      const costs = {};
      for(const section in this.form) {
        if(Array.isArray(this.form[section])) {
          if (section === 'mileage') {
            costs[section] = this.form[section].reduce((total, s) => total + (s.miles * s.rate || 0), 0);
            continue;
          }

          costs[section] = this.form[section].reduce((total, s) => total + (s.cost || 0), 0);
          continue;
        }

        if (section === 'labour') {
          costs[section] = Object.values(this.form[section]).reduce((total, s) => total + (s.hours * s.hourly_rate || 0), 0);
          costs['overheads'] = costs[section] * 0.2;
          continue;
        }

        // this will be a file
        if (section === 'soe_upload') {
          continue;
        }

        costs[section] = Object.values(this.form[section]).reduce((total, s) => total + (s.cost || 0), 0);
      }
      return costs;
    },

    /**
     * @description Get the total cost of all sections in the claim form. This is used to validate that the forecast does not exceed the claim total.
     * @returns {Number}
     */
    totalCost() {
      return Object.values(this.sectionCosts).reduce((total, cost) => total + cost, 0);
    },

    /**
     * @abstract Get the total claim cost after applying the intervention rate of 70%. This is used to validate that the forecast does not exceed the claim total.
     * @returns {Number}
     */
    claimTotal() {
      return (this.totalCost ?? 0) * .7;
    },

    /**
     * @description Get the total forecast for the current month and for delivery partners. This is used to validate that the forecast does not exceed the claim total and that the delivery partner forecast does not exceed the allocated budget.
     * @returns {Number}
     */
    forecastTotal() {
      return Object.values(this.form.forecast).flat().reduce((total, f) => total + (f.value || 0), 0);
    },

    /**
     * @description Get the claim status label and colour for the current claim. This is used to display the status of the claim in the UI.
     * @returns {{label: string, colour: string}}
     */
    claimStatus() {
      return {
        label: this.claim.status_label ? this.claim.status_label + ' v' + this.claim.version : 'Unknown',
        colour: (() => {
          switch(this.claim.status) {
            case 0: return 'primary';
            case 1:
            case 2: return 'neutral';
            case 3: return 'positive';
            case 4: return 'secondary';
            case 5: return 'negative';
            case 6:
            default: return 'grey';
          }
        })()
      }
    },
  },
  created() {
    document.title = `Project ${this.state.project.ref} Claims :: ${
      document.title
    }`;

    const unloadEvent = (event) => {
        if (this.dirty) {
          if(!confirm("You have unsaved changes. Are you sure you want to leave?")){
            event.preventDefault();
            event.returnValue = "";
          }
        }
    };

    Object.assign(this.form, this.claim.form ?? {});
    if (this.claim.status === 0) {
      this.setupStaffForm();
      this.setupDeliveryPartnersForm();
      this.setupForecast();

      window.addEventListener("beforeunload", unloadEvent);

      this.$once("hook:beforeDestroy", () => {
        window.removeEventListener("beforeunload", unloadEvent);
      });
  }
  },
  methods: {
    errorsForKey(key) {
      const keys = Object.keys(this.errors);
      return keys.filter((k) => k.startsWith(key)).length
    },

    totalForPeriod(period) {
      if (!period) {
        return 0.00;
      }

      if(period[0].type == "actual" && period[0].version == this.forecasts.current.revision) {
        return period.reduce((total, f) => total += (this.sectionCosts[f.key] || 0), 0);
      }

      return period.reduce((total, f) => total += (f.value || 0), 0);
    },

    totalForSection(section) {
      const values = Object.values(this.form.forecast).flat().filter(f => f.key === section);
      return values.reduce((total, f) =>  total += (f.value || 0) , 0);
    },

    /**
     * @description handleUpload and save in the same flow - this stops potential mismatching of data when a file is uploaded and not persisted to the claim
     * @param {object} entry - the entry object that the invoice is being uploaded for
     * @param {File} file
     * @returns {void}
     */
    handleUpload(entry, file) {
      entry.invoice = file;
      this.updateClaim();
    },

    /**
     * @description Setup the defaults for the staff form as id: StaffEntry. If an entry already exists, it will be used instead of the default.
     * @returns {void}
     */
    setupStaffForm() {
      // If there is already a claim form in use, use that instead of the default.
      // TODO: If the claim has not been submitted, we should "update" the existing claim with the new rates
      if (this.claim.form.staff) {
        this.$set(this.form, "labour", this.claim.form.labour);
      }
      this.staff.forEach((member) => {
        const existingEntry = this.form.labour[member.id];
        if (existingEntry && existingEntry.rate_version === member.rate_version) {
          return;
        }

        if (existingEntry && existingEntry.rate_version !== member.rate_version) {
          // If the rate version has changed, we should update the rates but keep the hours and description
          this.$set(this.form.labour, member.id, {
            ...existingEntry,
            rate_version: member.rate_version,
            day_rate: member.active_rate.day_rate,
            hourly_rate: member.active_rate.hourly_rate,
          });
          return;
        }

        this.$set(this.form.labour, member.id, {
          id: member.id,
          name: member.name,
          description: "",
          rate_version: member.rate_version,
          day_rate: member.active_rate.day_rate,
          hourly_rate: member.active_rate.hourly_rate,
          hours: 0,
        });
      });
    },

    /**
     * @description Setup the defaults for the delivery partners form as id: DeliveryPartnerEntry. If an entry already exists, it will be used instead of the default.
     * @returns {void}
     */
    setupDeliveryPartnersForm() {
      // If there is already a claim form in use, use that instead of the default.
      if(this.claim.form.delivery_partners) {
        this.$set(this.form, "delivery_partners", this.claim.form.delivery_partners);
      }
      this.deliveryPartners.forEach((partner) => {
        const existingEntry = this.form.delivery_partners[partner.id];
        if (existingEntry) {
          return;
        }

        this.$set(this.form.delivery_partners, partner.id, {
          id: partner.id,
          name: partner.name,
          description: "",
          cost: 0,
          invoice: null,
        });
      });
    },

    /**
     * @description Setup the defaults for the forecast form. If an entry already exists, it will be used instead of the default.
     */
    setupForecast() {
      const revision = this.forecasts?.current.revision;

      if(this.claim.form.forecast) {
        this.$set(this.form, "forecast", {
          ...this.forecasts.actuals,
          ...this.claim.form.forecast,
        });
        return;
      }


      if (!revision) {
        // eslint-disable-next-line no-console
        console.error("Forecasts are not properly defined. Current month or version is missing.");
        return;
      }

      this.$set(this.form, "forecast", {
        ...this.forecasts.actuals,
        ...this.forecasts.revisions[revision]
      });
    },

    /**
     * @description Set actual for this period to the totals of the form
     * @returns {void}
     */
    setActuals() {
      const forecastPeriod = this.forecasts.current.month;
      this.form.forecast[forecastPeriod].map((entry) => {
        entry.value = this.sectionCosts[entry.key] || 0;
      });
    },

    /**
     * @description Process claim submission. This will submit the claim for approval and prevent further edits.
     * @returns {void}
     */
    awardClaim() {
      if(!this.canApprove) return;

      if(!confirm("Are you sure you want to approve this claim? This action cannot be undone.")) return;

      this.showReviewModal = false;
      axios
        .patch(window.location, { feedback: this.feedback })
        // eslint-disable-next-line no-console
        .then(() => {
          Turbolinks.visit(window.location);
          this.showReviewModal = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 422) {
            this.errors = error.response.data.errors;
          } else {
            // eslint-disable-next-line no-console
            console.error(error);
            alert("An unexpected error occurred. Please try again.");
          }
        });
    },

    rejectClaim() {
      if(!this.canApprove) return;

      if(!confirm("Are you sure you want to reject this claim?")) return;

      this.showReviewModal = false;
      axios({
        method: 'DELETE',
        url: window.location,
        data: {
          feedback: this.feedback,
        }
      })
        // eslint-disable-next-line no-console
        .then(() => {
          Turbolinks.visit(route("monitoring.show", { project: this.project.uuid }));
          this.showReviewModal = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 422) {
            this.errors = error.response.data.errors;
          } else {
            // eslint-disable-next-line no-console
            console.error(error);
            alert("An unexpected error occurred. Please try again.");
          }
        });
    },

    /**
     * @description get the error for a given entry and field. This will return the first error for the field if it exists, otherwise null.
     */
    errorFor(field) {
      return this.errors?.[field]?.[0];
    },

    /**
     * @description Create a prefixed unix timestamp for use as a unique key.
     * @param {string} prefix
     * @returns {string}
     */
    uniqueKey(prefix) {
      if (!prefix) {
        throw new Error("Prefix is required for uniqueKey");
      }
      return `${prefix}_${Date.now()}`;
    },

    /**
     * @description Requires an invoice to be uploaded for the given entry if the cost is over £150.
     * @param {number} cost
     * @returns {boolean}
     */
    requiresInvoice(cost) {
      return cost >= 150;
    },

    /**
     * @description Converts a number to a decimal string with the given number of points.
     * @param {number} value
     * @param {number} points
     * @returns {string}
     */
    toDecimal(value, points = 2) {
      return parseFloat(value || 0).toFixed(points);
    },

    /**
     * @description Creates a new entry for the given section and adds it to the form.
     * @param {section_types} section
     * @returns {void}
     */
    newEntryFor(section) {
      if (!this.canEdit) {
        return;
      }
      switch (section) {
        case "sub_contractor":
          this.form.sub_contractors.push({
            ...subContractorEntryTemplate,
            id: this.uniqueKey("sub_contractor"),
          });
          break;
        case "travel":
          this.form.travel.push({
            ...travelEntryTemplate,
            id: this.uniqueKey("travel"),
          });
          break;
        case "mileage":
          this.form.mileage.push({
            ...mileageEntryTemplate,
            id: this.uniqueKey("mileage"),
          });
          break;
        case "material":
          this.form.materials.push({
            ...materialEntryTemplate,
            id: this.uniqueKey("material"),
          });
          break;
        case "other_cost":
          this.form.other_costs.push({
            ...otherCostEntryTemplate,
            id: this.uniqueKey("other_cost"),
          });
          break;
      }
    },
  }
};

/** @typedef {object} Forecasts
 * @property {boolean} initials_completed
 * @property {{month: string, revision: number, submitted: false}} current
 * @property {object} revisions
 * @property {object} actuals
 */

/** @typedef {object} ForecastOption
 * @property {number} id
 * @property {string} competition_id
 * @property {string} key
 * @property {string} name
 * @property {string} description
 */

/** @type {'sub_contractor' | 'travel' | 'mileage' | 'material' | 'other_cost' } section_types  */

/** @typedef {object} Invoice
 * @property {number} id
 * @property {number} competition_project_id
 * @property {string} parent_key
 * @property {string} key
 * @property {string} url
 * @property {string} path
 * @property {string} name
 * @property {string} size
 * @property {string} extension
 * @property {string} mime_type
 * @property {string} created_at
 * @property {string} updated_at
 * @property {number} created_by
 * @property {number} updated_by
 */

/** @typedef {object} StaffEntry
 * @property {number} id
 * @property {string} name
 * @property {string} role
 * @property {object} description
 * @property {number} rate_version
 * @property {object} day_rate
 * @property {object} hourly_rate
 * @property {hours} number
 */

/** @typedef {object} SubContractorEntry
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {number} cost
 * @property {Invoice|null} invoice
*/
const subContractorEntryTemplate = {
  id: null,
  name: "",
  description: "",
  cost: null,
  invoice: null,
};

/** @typedef {object} TravelEntry
 * @property {number} id
 * @property {string} description
 * @property {number} cost
 * @property {Invoice|null} invoice
 */
const travelEntryTemplate = {
  id: null,
  description: "",
  cost: null,
  invoice: null,
};

/** @typedef {object} MileageEntry
 * @property {number} id
 * @property {string} description
 * @property {number} miles
 * @property {Invoice|null} rate
 */
const mileageEntryTemplate = {
  id: null,
  description: "",
  miles: null,
  rate: .55,
  invoice: null,
};

/** @typedef {object} MaterialEntry
 * @property {number} id
 * @property {string} description
 * @property {number} cost
 * @property {Invoice|null} invoice
 */
const materialEntryTemplate = {
  id: null,
  description: "",
  cost: null,
  invoice: null,
};

/** @typedef {object} OtherCostEntry
 * @property {number} id
 * @property {string} description
 * @property {number} cost
 * @property {Invoice} invoice
 */
const otherCostEntryTemplate = {
  id: null,
  description: "",
  cost: null,
  invoice: null,
};

/** @typedef {object} DeliveryPartnerEntry
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {number} cost
 * @property {Invoice} invoice
*/

/** @typedef {object} ClaimForm
* @property {Object.<number, StaffEntry} staff
 * @property {SubContractorEntry[]} sub_contractors
 * @property {TravelEntry[]} travel
 * @property {MileageEntry[]} mileage
 * @property {MaterialEntry[]} materials
 * @property {OtherCostEntry[]} other_costs
 * @property {Object.<number, DeliveryPartnerEntry>} delivery_partners
 * @property {FileUpload} soe_upload
*/
</script>

<template>
  <app-layout
    :state="state"
    header-type="landing"
  >
    <template #header>
      <icon-link
        :href="route('monitoring.show', { project: project.uuid })"
        icon="arrow-thin-left-circle"
        class="icons-white"
      >
        Back to project
      </icon-link>
      <h1>
        Project {{ project.ref }}
        <div class="flex items-baseline mt-1">
          <span
            v-if="project.stream"
            style="padding-block: .125rem"
            class="border rounded px-2 align-bottom text-xs inline-block leading-none mr-2"
            :style="getStreamColorsByKey(project.stream)"
          >
            {{ getStreamNameByKey(project.stream) }}
          </span>
          <small class="font-normal text-xs opacity-50 leading-none">{{ state.competition.title }}</small>
        </div>
      </h1>
    </template>
    <form
      class="container py-8"
      @submit.prevent="() => {}"
    >
      <div class="flex items-baseline mb-6">
        <h2>
          <data-provider
            v-slot="{ data }"
            :source="claimStatus"
          >
            <app-badge
              :type="data.colour"
              class="mr-4"
              slot-classes="text-lg py-2"
            >
              {{ data.label }}
            </app-badge>
          </data-provider>
          Claims Form for {{ period }}
        </h2>
        <nav class="flex items-baseline ml-6">
          <a
            v-for="section in sections"
            :key="section.key"
            :href="`#${section.key}`"
            target="_self"
            :class="['mr-4 no-underline', {
              'text-primary-500 hover:text-primary-700': !section.errors,
              'text-negative-500 hover:text-negative-700': section.errors > 0,
            }]"
          >
            <span class="underline">{{ section.name }}</span>
            <sup
              v-if="!!section.errors"
              class="bg-negative-200 text-2xs p-px rounded-full no-underline"
            >
              {{ section.errors }}
            </sup>
          </a>
        </nav>
      </div>

      <!-- Labour Costs -->
      <claims-section-container
        id="labour"
        class="mb-16"
        :errors="errors"
      >
        <template #header>
          <claims-section-header
            icon="labour"
            :columns="['Labour Costs', 'Description', ':Hours', ':Day Rate', ':Hourly Rate', ':Total']"
          />
        </template>
        <tbody>
          <tr
            v-for="s in form.labour"
            :key="s.id"
          >
            <td>
              {{ s.name }} {{ s.role }}
            </td>
            <td
              :class="['w-full', {
                'has-error': !!errorFor(`labour.${s.id}.description`)
              }]"
            >
              <span
                v-if="!canEdit"
                v-html="s.description"
              />
              <input
                v-else
                :id="s.id + '_description'"
                v-model.trim="s.description"
                type="text"
                placeholder="Description of the work undertaken"
                :disabled="processing || !canEdit"
              />
            </td>
            <td
              :class="{
                'has-error': !!errorFor(`labour.${s.id}.hours`)
              }"
            >
              <input
                :id="s.id + '_hours'"
                v-model.number="s.hours"
                type="number"
                step=".25"
                placeholder="--"
                class="w-16 text-right"
                :disabled="processing || !canEdit"
              />
            </td>
            <td><span class="currency">£ <span>{{ toDecimal(s.day_rate) }}</span></span></td>
            <td><span class="currency">£ <span>{{ toDecimal(s.hourly_rate) }}</span></span></td>
            <td><span class="currency">£ <span>{{ toDecimal(s.hours * s.hourly_rate) }}</span></span></td>
          </tr>
        </tbody>
        <template #gutter>
          <p>
            Please submit a <a href="">change request</a> if you would like to alter your staff and/or day rates. The will need to be approved by you PMQ before the change are reflected above.
          </p>
        </template>
      </claims-section-container>
      <!-- .END -->

      <!-- Sub Contactors -->
      <claims-section-container
        id="sub_contractors"
        class="mb-16"
        :errors="errors"
      >
        <claims-section-header
          icon="sub-contractors"
          :columns="['Sub Contractors', 'Description', 'Cost', 'Invoice']"
          :has-delete="canEdit"
        />
        <tbody>
          <tr
            v-if="form.sub_contractors.length === 0"
            class="text-center"
          >
            <td
              colspan="5"
              class="empty"
            >
              No sub contractors have been added to this claim.
            </td>
          </tr>
          <tr
            v-for="(item, index) in form.sub_contractors"
            v-else
            :key="item.id"
          >
            <td
              v-if="canEdit"
              width="20"
              style="padding: 0 .5rem;"
            >
              <button
                type="button"
                class="remove"
                @click="form.sub_contractors.splice(index, 1)"
              >
                &minus;
                <span class="sr-only">Remove Item</span>
              </button>
            </td>
            <td
              :class="{
                'has-error': !!errorFor(`sub_contractors.${index}.name`)
              }"
            >
              <input
                :id="item.id + '_name'"
                v-model.trim="item.name"
                type="text"
                placeholder="Name of the sub contractor"
                :disabled="processing || !canEdit"
              />
            </td>
            <td
              :class="{
                'has-error': !!errorFor(`sub_contractors.${index}.description`)
              }"
            >
              <span
                v-if="!canEdit"
                v-html="item.description"
              />
              <input
                v-else
                :id="item.id + '_description'"
                v-model.trim="item.description"
                type="text"
                placeholder="Description of the work undertaken"
                :disabled="processing || !canEdit"
              />
            </td>
            <td
              width="150"
              :class="{
                'has-error': !!errorFor(`sub_contractors.${index}.cost`)
              }"
            >
              <span class="currency">
                £
                <input
                  :id="item.id + '_cost'"
                  v-model.number="item.cost"
                  type="number"
                  step=".01"
                  class="w-24 text-right"
                  :disabled="processing || !canEdit"
                />
              </span>
            </td>
            <td
              width="250"
              :class="[{
                'has-error': !!errorFor(`sub_contractors.${index}.invoice`)
              }]"
            >
              <claims-section-invoice
                v-if="requiresInvoice(item.cost)"
                :id="item.id + '_invoice'"
                class="px-4"
                :parent-key="`claim_invoice_`+claim.id"
                :file-key="item.id+`_invoice`"
                :invoice="item.invoice"
                :disabled="processing || !canEdit"
                @upload="item.invoice = $event"
              />
            </td>
          </tr>
          <tr v-if="canEdit">
            <td colspan="5">
              <button
                type="button"
                class="action"
                @click="newEntryFor('sub_contractor')"
              >
                &plus; Add Item
              </button>
            </td>
          </tr>
        </tbody>
      </claims-section-container>
      <!-- .END -->

      <!-- Travel -->
      <claims-section-container
        id="travel"
        class="mb-16"
        :errors="errors"
      >
        <claims-section-header
          icon="travel"
          :columns="['Travel & Subsistance', 'Cost', 'Invoice']"
          :has-delete="canEdit"
        />
        <tbody>
          <tr
            v-if="form.travel.length === 0"
            class="text-center"
          >
            <td
              colspan="4"
              class="empty"
            >
              No travel or sustenance costs have been added to this claim.
            </td>
          </tr>
          <tr
            v-for="(item, index) in form.travel"
            v-else
            :key="item.id"
          >
            <td
              v-if="canEdit"
              width="20"
              style="padding: 0 .5rem;"
            >
              <button
                type="button"
                class="remove"
                @click="form.travel.splice(index, 1)"
              >
                &minus;
                <span class="sr-only">Remove Item</span>
              </button>
            </td>
            <td
              :class="{
                'has-error': !!errorFor(`travel.${index}.description`)
              }"
            >
              <span
                v-if="!canEdit"
                v-html="item.description"
              />
              <input
                v-else
                :id="item.id + '_description'"
                v-model.trim="item.description"
                type="text"
                placeholder="Description of the travel or sustenance"
                :disabled="processing || !canEdit"
              />
            </td>
            <td
              width="150"
              :class="{
                'has-error': !!errorFor(`travel.${index}.cost`)
              }"
            >
              <span class="currency">
                £
                <input
                  :id="item.id + '_cost'"
                  v-model.number="item.cost"
                  type="number"
                  step=".01"
                  class="w-24 text-right"
                  placeholder="--"
                  :disabled="processing || !canEdit"
                />
              </span>
            </td>
            <td
              width="250"
              :class="{
                'has-error': !!errorFor(`travel.${index}.invoice`)
              }"
            >
              <claims-section-invoice
                v-if="requiresInvoice(item.cost)"
                :id="item.id + '_invoice'"
                class="px-4"
                :parent-key="`claim_invoice_`+claim.id"
                :file-key="item.id+`_invoice`"
                :invoice="item.invoice"
                :disabled="processing || !canEdit"
                @upload="item.invoice = $event"
              />
            </td>
          </tr>
          <tr v-if="canEdit">
            <td colspan="4">
              <button
                type="button"
                class="action"
                @click="newEntryFor('travel')"
              >
                &plus; Add Item
              </button>
            </td>
          </tr>
        </tbody>
      </claims-section-container>
      <!-- .END -->

      <!-- Mileage -->
      <claims-section-container
        id="mileage"
        class="mb-16"
        :errors="errors"
      >
        <claims-section-header
          icon="mileage"
          :columns="['ICE Mileage', 'Miles', 'Rate', 'Cost']"
          :has-delete="canEdit"
        />
        <tbody>
          <tr
            v-if="form.mileage.length === 0"
            class="text-center"
          >
            <td
              colspan="5"
              class="empty"
            >
              No journeys have been added to this claim.
            </td>
          </tr>
          <tr
            v-for="(item, index) in form.mileage"
            v-else
            :key="item.id"
          >
            <td
              v-if="canEdit"
              width="20"
              style="padding: 0 .5rem;"
            >
              <button
                type="button"
                class="remove"
                @click="form.mileage.splice(index, 1)"
              >
                &minus;
                <span class="sr-only">Remove Item</span>
              </button>
            </td>
            <td
              :class="{
                'has-error': !!errorFor(`mileage.${index}.description`)
              }"
            >
              <span
                v-if="!canEdit"
                v-html="item.description"
              />
              <input
                v-else
                :id="item.id + '_description'"
                v-model.trim="item.description"
                type="text"
                placeholder="Description of the journey"
                :disabled="processing || !canEdit"
              />
            </td>
            <td
              width="150"
              :class="{
                'has-error': !!errorFor(`mileage.${index}.miles`)
              }"
            >
              <input
                :id="item.id + '_miles'"
                v-model.trim="item.miles"
                type="number"
                step="1"
                placeholder="--"
                class="text-right"
                :disabled="processing || !canEdit"
              />
            </td>
            <td width="150">
              <span class="currency">
                £
                <span>{{ toDecimal(item.rate) }}</span>
              </span>
            </td>
            <td width="150">
              <span class="currency">
                £
                <span>{{ toDecimal(form.mileage[index].miles* item.rate) }}</span>
              </span>
            </td>
          </tr>
          <tr v-if="canEdit">
            <td colspan="5">
              <button
                type="button"
                class="action"
                @click="newEntryFor('mileage')"
              >
                &plus; Add Item
              </button>
            </td>
          </tr>
        </tbody>
      </claims-section-container>
      <!-- .END -->

      <!-- Materials -->
      <claims-section-container
        id="materials"
        class="mb-16"
        :errors="errors"
      >
        <claims-section-header
          icon="materials"
          :columns="['Materials', 'Cost', 'Invoice']"
          :has-delete="canEdit"
        />
        <tbody>
          <tr
            v-if="form.materials.length === 0"
            class="text-center"
          >
            <td
              colspan="4"
              class="empty"
            >
              No materials have been added to this claim.
            </td>
          </tr>
          <tr
            v-for="(item, index) in form.materials"
            v-else
            :key="item.id"
          >
            <td
              v-if="canEdit"
              width="20"
              style="padding: 0 .5rem;"
            >
              <button
                type="button"
                class="remove"
                @click="form.materials.splice(index, 1)"
              >
                &minus;
                <span class="sr-only">Remove Item</span>
              </button>
            </td>
            <td
              :class="{
                'has-error': !!errorFor(`materials.${index}.description`)
              }"
            >
              <span
                v-if="!canEdit"
                v-html="item.description"
              />
              <input
                v-else
                :id="item.id + '_description'"
                v-model.trim="item.description"
                type="text"
                placeholder="Description of the material"
                :disabled="processing || !canEdit"
              />
            </td>
            <td
              width="150"
              :class="{
                'has-error': !!errorFor(`materials.${index}.cost`)
              }"
            >
              <span class="currency">
                £
                <input
                  :id="item.id + '_cost'"
                  v-model.number="item.cost"
                  type="number"
                  step=".01"
                  class="w-24 text-right"
                  :disabled="processing || !canEdit"
                />
              </span>
            </td>
            <td
              width="250"
              :class="{
                'has-error': !!errorFor(`materials.${index}.invoice`)
              }"
            >
              <claims-section-invoice
                v-if="requiresInvoice(item.cost)"
                :id="item.id + '_invoice'"
                class="px-4"
                :parent-key="`claim_invoice_`+claim.id"
                :file-key="item.id+`_invoice`"
                :invoice="item.invoice"
                :disabled="processing || !canEdit"
                @upload="item.invoice = $event"
              />
            </td>
          </tr>
          <tr v-if="canEdit">
            <td colspan="4">
              <button
                type="button"
                class="action"
                @click="newEntryFor('material')"
              >
                &plus; Add Item
              </button>
            </td>
          </tr>
        </tbody>
      </claims-section-container>
      <!-- .END -->

      <!-- Other Costs -->
      <claims-section-container
        id="other_costs"
        class="mb-16"
        :errors="errors"
      >
        <claims-section-header
          icon="other-costs"
          :columns="['Other Costs', 'Cost', 'Invoice']"
          :has-delete="canEdit"
        />
        <tbody>
          <tr
            v-if="form.other_costs.length === 0"
            class="text-center"
          >
            <td
              colspan="4"
              class="empty"
            >
              No other costs have been added to this claim.
            </td>
          </tr>
          <tr
            v-for="(item, index) in form.other_costs"
            v-else
            :key="item.id"
          >
            <td
              v-if="canEdit"
              width="20"
              style="padding: 0 .5rem;"
            >
              <button
                type="button"
                class="remove"
                @click="form.other_costs.splice(index, 1)"
              >
                &minus;
                <span class="sr-only">Remove Item</span>
              </button>
            </td>
            <td
              :class="{
                'has-error': !!errorFor(`other_costs.${index}.description`)
              }"
            >
              <span
                v-if="!canEdit"
                v-html="item.description"
              />
              <input
                v-else
                :id="item.id + '_description'"
                v-model.trim="item.description"
                type="text"
                placeholder="Description of cost"
                :disabled="processing || !canEdit"
              />
            </td>
            <td
              width="150"
              :class="{
                'has-error': !!errorFor(`other_costs.${index}.cost`)
              }"
            >
              <span class="currency">
                £
                <input
                  :id="item.id + '_cost'"
                  v-model.number="item.cost"
                  type="number"
                  step=".01"
                  class="w-24 text-right"
                  :disabled="processing || !canEdit"
                />
              </span>
            </td>
            <td width="250">
              <claims-section-invoice
                v-if="requiresInvoice(item.cost)"
                :id="item.id + '_invoice'"
                class="px-4"
                :parent-key="`claim_invoice_`+claim.id"
                :file-key="item.id+`_invoice`"
                :invoice="item.invoice"
                :disabled="processing || !canEdit"
                @upload="item.invoice = $event"
              />
            </td>
          </tr>
          <tr v-if="canEdit">
            <td colspan="4">
              <button
                type="button"
                class="action"
                @click="newEntryFor('other_cost')"
              >
                &plus; Add Item
              </button>
            </td>
          </tr>
        </tbody>
      </claims-section-container>
      <!-- .END -->

      <!-- Delivery Partners -->
      <claims-section-container
        id="delivery_partners"
        class="mb-16"
        :errors="errors"
      >
        <claims-section-header
          icon="delivery-partners"
          :columns="['Delivery Partners', 'Description', 'Cost', 'Invoice']"
        />
        <tbody>
          <tr
            v-for="dp in form.delivery_partners"
            :key="dp.id"
          >
            <td>
              {{ dp.name }}
            </td>
            <td
              :class="{
                'has-error': !!errorFor(`delivery_partners.${dp.id}.description`)
              }"
            >
              <span
                v-if="!canEdit && dp.description"
                v-html="dp.description"
              />
              <input
                v-else
                :id="dp.id + '_description'"
                v-model.trim="dp.description"
                type="text"
                placeholder="Description of the work undertaken"
                :disabled="processing || !canEdit"
              />
            </td>
            <td
              width="150"
              :class="{
                'has-error': !!errorFor(`delivery_partners.${dp.id}.cost`)
              }"
            >
              <span class="currency">
                £
                <input
                  :id="dp.id + '_cost'"
                  v-model.number="dp.cost"
                  type="number"
                  step=".01"
                  class="w-24 text-right"
                  :disabled="processing || !canEdit"
                />
              </span>
            </td>
            <td
              width="250"
              :class="{
                'has-error': !!errorFor(`delivery_partners.${dp.id}.invoice`)
              }"
            >
              <claims-section-invoice
                v-if="requiresInvoice(dp.cost)"
                :id="dp.id + '_invoice'"
                class="px-4"
                :parent-key="`claim_invoice_`+claim.id"
                :file-key="`delivery_partners_`+dp.id+`_invoice`"
                :invoice="dp.invoice"
                :disabled="processing || !canEdit"
                @upload="handleUpload(dp, $event)"
              />
            </td>
          </tr>
        </tbody>
      </claims-section-container>
      <!-- .END -->

      <!-- Forecasting -->
      <claims-section-container
        id="forecast"
        class="mb-16"
        :errors="errors"
      >
        <claims-section-header
          icon="forecast"
          :columns="['Expenditure Forecast', ...Object.keys(form.forecast).map(i => ':' + i + ':'), ':Total', ':Grant']"
        />
        <tbody>
          <tr
            v-for="(option, index) in options"
            :key="option.key"
          >
            <td>{{ option.name }}</td>
            <data-provider
              v-for="(fp, fpk) in form.forecast"
              :key="fpk + '_' + option.key"
              v-slot="{ data: entry }"
              :source="fp.find((f) => f.key === option.key)"
            >
              <td
                :class="{
                  'bg-grey-50': entry.type == 'actual' && entry.version != forecasts.current.revision,
                  'bg-primary-50': entry.type == 'actual' && entry.version == forecasts.current.revision,
                  'bg-neutral-50': entry.type == 'revised' && [null, undefined, ''].includes(entry.value),
                  'error': !!errorFor(`forecast.${fpk}.${index}.value`)
                }"
                style="width: 8.25rem;"
              >
                <span class="currency">£
                  <template v-if="entry.type == 'actual' && entry.version != forecasts.current.revision">
                    <span>{{ toDecimal(entry.value || 0.00) }}</span>
                  </template>
                  <template v-else-if="entry.type == 'actual' && entry.version == forecasts.current.revision">
                    <span>{{ toDecimal(sectionCosts[option.key] || 0) }}</span>
                  </template>
                  <template v-else-if="option.key == 'overheads'">
                    <input
                      :id="`${entry.id}:${entry.key}:${entry.type}`"
                      readonly
                      type="number"
                      class="w-full text-right bg-transparent focus:outline-none p-0"
                      inputmode="decimal"
                      step="0.01"
                      min="0"
                      max="100000"
                      :value="toDecimal(entry.value)"
                      placeholder="--"
                    />
                  </template>
                  <template v-else>
                    <input
                      :id="`${entry.id}:${entry.key}:${entry.type}`"
                      v-model.number="entry.value"
                      type="number"
                      class="w-full text-right bg-transparent focus:outline-none p-0"
                      inputmode="decimal"
                      step="0.01"
                      min="0"
                      max="100000"
                      :disabled="processing || !canEdit"
                      placeholder="--"
                    />
                  </template>
                </span>
              </td>
            </data-provider>
            <td>
              <span class="currency">£
                <span>{{ toDecimal(totalForSection(option.key)) }}</span>
              </span>
            </td>
            <td>
              <span class="currency">£
                <span>{{ toDecimal(totalForSection(option.key) * .7) }}</span>
              </span>
            </td>
          </tr>
          <tr class="bg-grey-900 text-white font-bold">
            <td class="font-bold">
              Total
            </td>
            <td
              v-for="(fp, fpk) in form.forecast"
              :key="fpk + '_total'"
              class="text-right"
            >
              <span class="currency">£
                <span>{{ toDecimal(totalForPeriod(fp)) }}</span>
              </span>
            </td>
            <td>
              <span class="currency">£
                <span>{{ toDecimal(forecastTotal) }}</span>
              </span>
            </td>
            <td>
              <span class="currency">£
                <span>{{ toDecimal(forecastTotal * .7) }}</span>
              </span>
            </td>
          </tr>
          <tr class="bg-grey-900 text-white font-bold">
            <td class="font-bold">
              Grant Claim (70%)
            </td>
            <td
              v-for="(fp, fpk) in form.forecast"
              :key="fpk + '_total'"
              class="text-right"
            >
              <span class="currency">£
                <span>{{ toDecimal(totalForPeriod(fp) * .7) }}</span>
              </span>
            </td>
            <td>
              <span class="currency">£
                <span>{{ toDecimal(forecastTotal * .7) }}</span>
              </span>
            </td>
            <td>
              <span class="currency">£
                <span>{{ toDecimal(forecastTotal * .7) }}</span>
              </span>
            </td>
          </tr>
        </tbody>
      </claims-section-container>

      <!-- start totals-->
      <div class="w-1/3 ml-auto">
        <dl class="mb-12">
          <div
            v-if="requiresSoe"
            :class="['flex justify-between py-2 rounded-lg border mb-6 -mx-4 px-4 items-center', {
              'border-primary-500 text-primary-700 bg-primary-50': !errorFor('soe_upload'),
              'border-negative-500 text-negative-700 bg-negative-50': !!errorFor('soe_upload'),
            }]"
          >
            <dt class="text-xl font-bold whitespace-no-wrap mr-6">
              <abbr title="Statement of Expenditure">SoE</abbr> Upload
            </dt>
            <dd style="min-width:33%">
              <claims-section-invoice
                id="soe_upload"
                :parent-key="`soe_upload_`+claim.id"
                file-key="soe_upload"
                :invoice="form.soe_upload"
                :disabled="processing || !canEdit"
                :error="errorFor('soe_upload')"
                @upload="form.soe_upload = $event"
              />
            </dd>
          </div>
          <div class="flex justify-between py-2 text-xl font-bold border-b border-black">
            <dt>Total Cost for {{ period }}</dt>
            <dd class="currency">
              £
              {{ toDecimal(totalCost) }}
            </dd>
          </div>
          <div class="flex justify-between py-4 text-3xl font-bold border-b border-black">
            <dt>Claim Total for {{ period }}</dt>
            <dd class="currency">
              £ {{ toDecimal(claimTotal) }}
            </dd>
          </div>
        </dl>
      </div>
      <!-- .END -->

      <!-- start actions footer -->
      <div class="sticky flex justify-between items-center z-10 pin-b py-6 border-t bg-grey-100">
        <p>
          <strong>Submitted By:</strong> {{ claim.submittee ? claim.submittee.name : "--" }} |
          <strong>Reviewed By:</strong> {{ claim.reviewee ? claim.reviewee.name : 'Not Yet Reviewed' }}
          <strong>Approved By:</strong> {{ claim.approvee ? claim.approvee.name : 'Not Yet Approved' }}
        </p>
        <div class="w-1/5 ml-auto flex justify-between">
          <app-button
            type="button"
            class="w-1/2"
            @click="showReviewModal = true"
          >
            {{ canApprove ? 'Approve / Query' : 'Review' }}
            <portal to="modals">
              <app-modal
                v-if="showReviewModal"
                container-class="max-w-lg w-screen"
                @close="showReviewModal = false"
              >
                <app-card
                  class="my-12"
                  status="positive"
                  tag="form"
                  @submit.prevent="() => {}"
                >
                  <header class="px-6 py-4 border-b flex">
                    <h1 class="text-xl flex-1">
                      Claims Feedback for {{ period }}
                    </h1>
                  </header>

                  <div
                    v-for="error in errors"
                    :key="error"
                    class="bg-negative-100 text-negative-800 border-t border-b border-negative-300 py-2 px-6 -my-px"
                  >
                    <strong>ERROR:</strong> {{ error[0] }}
                  </div>

                  <div
                    v-for="(section, index) in Object.keys(feedback)"
                    :key="`feedback_${index}`"
                  >
                    <label
                      :for="`feedback_${section}`"
                      :class="['py-2 px-6 block text-xs tracking-wide font-bold uppercase bg-primary-100 text-primary-800', {
                        'bg-primary-100 text-primary-800': !!errorFor(`feedback.${section}`),
                        'bg-negative-100 text-negative-800': errorFor(`feedback.${section}`),
                      }]"
                    >
                      {{ section.charAt(0).toUpperCase() + section.slice(1).replace("_", " ") }} Comments
                    </label>
                    <details
                      v-if="claim.reviewee && claim.review_comments.reviewee && claim.review_comments.reviewee[section]"
                      class="px-6 py-2 bg-grey-100"
                      name="reviewee-comments"
                    >
                      <summary class="text-sm font-bold">
                        Reviewer: {{ claim.reviewee.name }}'s Comments
                      </summary>
                      <div v-html="parseMarkdown(claim.review_comments.reviewee[section] || '')" />
                    </details>
                    <details
                      v-if="claim.approvee && claim.review_comments.approvee && claim.review_comments.approvee[section]"
                      class="px-6 py-2 bg-grey-100"
                      name="approvee-comments"
                    >
                      <summary class="text-sm font-bold">
                        Approver: {{ claim.approvee.name }}'s Comments
                      </summary>
                      <div v-html="parseMarkdown(claim.review_comments.approvee[section] || '')" />
                    </details>
                    <textarea
                      :id="`feedback_${section}`"
                      v-model.trim="feedback[section]"
                      rows="3"
                      :placeholder="`Add comments for ${ section.replace('_', ' ') }...`"
                      class="block w-full py-4 px-6 focus:outline-none focus:outline focus:outline-secondary-500 focus:outline-2 focus:outline-offset-0 resize-none"
                      style="field-sizing: content; min-height: 5rem"
                      :disabled="processing || !canApprove"
                    ></textarea>
                  </div>

                  <footer
                    class="sticky pin-b flex justify-between p-6 border-t z-10 bg-white"
                    style="gap: 1rem"
                  >
                    <app-button
                      type="button"
                      colour="delete-fill"
                      class="mr-auto"
                      :disabled="processing || !canApprove"
                      @click="rejectClaim"
                    >
                      Send Query
                    </app-button>
                    <app-button
                      type="submit"
                      colour="primary"
                      class="ml-auto"
                      :disabled="processing || !canApprove"
                      @click="awardClaim"
                    >
                      Approve Claim
                    </app-button>
                  </footer>
                </app-card>
              </app-modal>
            </portal>
          </app-button>
        </div>
      </div>
      <!-- .END -->
    </form>
  </app-layout>
</template>

<style scoped>
  .blur {
    filter: blur(.25rem);
    pointer-events: none;
  }
</style>
